import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Row, Col, Button } from 'antd';
import { GetAPI } from '../../../redux';
import { useDispatch } from 'react-redux';
import { RedoOutlined } from '@ant-design/icons';
import { CheckCircleIcon } from "@solar-icons/react/outline/check-circle";
import { DangerCircleIcon } from "@solar-icons/react/outline/danger-circle";
import { PlaneIcon } from "@solar-icons/react/outline/plane";
import { InboxIcon } from "@solar-icons/react/outline/inbox";
import { RouteIcon } from "@solar-icons/react/outline/route";
import { BaseColor } from '../../../config/env';
import TablePage from '../../../components/pages/TablePage';

const { Title, Text } = Typography;

const defaultMonit = {
    export: { success: 0, error: 0 },
    import: { success: 0, error: 0 },
    distribusi: { success: 0, error: 0 }
};

const hexToRgba = (hex = '#118B50', alpha = 1) => {
    const sanitized = hex.replace('#', '');
    const full = sanitized.length === 3
        ? sanitized.split('').map(ch => `${ch}${ch}`).join('')
        : sanitized.padEnd(6, '0');
    const num = parseInt(full, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const createStyles = (baseColor) => ({
    page: {
        position: 'relative',
        padding: 32,
        borderRadius: 20,
        minHeight: '60vh'
    },
    glowOne: {
        display: 'none'
    },
    glowTwo: {
        display: 'none'
    },
    content: {
        position: 'relative',
        zIndex: 2
    },
    hero: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 32
    },
    heroTitle: {
        color: '#0f172a',
        margin: 0
    },
    heroSubtitle: {
        color: '#475467',
        margin: 0
    },
    refreshButton: {
        borderColor: hexToRgba(baseColor, 0.35),
        color: baseColor,
        borderRadius: 999,
        paddingInline: 20,
        boxShadow: 'none'
    },
    // Figma Card/Export (18:551): white 28px radius, 28px padding, subtle border + soft shadow
    card: () => ({
        background: '#ffffff',
        border: '1px solid #eef2f0',
        borderRadius: 28,
        padding: 28,
        boxShadow: '0px 4px 6px rgba(10,38,26,0.05), 0px 1px 1px rgba(10,38,26,0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
        height: '100%'
    }),
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    titleGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: 12
    },
    iconChip: {
        width: 44,
        height: 44,
        borderRadius: 14,
        background: hexToRgba(baseColor, 0.08),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    },
    cardTitle: {
        color: '#141a17',
        fontSize: 18,
        fontWeight: 500,
        fontFamily: "Poppins, 'Inter', sans-serif",
        lineHeight: '26px',
        letterSpacing: '-0.045px',
        margin: 0
    },
    liveBadge: {
        background: hexToRgba(baseColor, 0.08),
        height: 28,
        borderRadius: 999,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingLeft: 10,
        paddingRight: 12
    },
    liveDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: baseColor
    },
    liveText: {
        color: baseColor,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: "Inter, sans-serif",
        lineHeight: '16px'
    },
    totalLabel: {
        color: '#4e5a55',
        fontSize: 14,
        fontFamily: "Inter, sans-serif",
        lineHeight: '20px'
    },
    totalValue: {
        color: '#141a17',
        fontSize: 36,
        fontWeight: 500,
        fontFamily: "Roboto, 'Inter', sans-serif",
        lineHeight: '44px',
        letterSpacing: '-0.36px',
        margin: 0
    },
    progressTrack: {
        display: 'flex',
        gap: 4,
        width: '100%'
    },
    breakdown: {
        display: 'flex',
        gap: 12,
        width: '100%'
    },
    successBox: {
        flex: 1,
        minWidth: 0,
        background: hexToRgba(baseColor, 0.08),
        borderRadius: 18,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    errorBox: {
        flex: 1,
        minWidth: 0,
        background: '#fef2f2',
        borderRadius: 18,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    breakdownLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        color: '#4e5a55',
        fontSize: 12,
        fontWeight: 500,
        fontFamily: "Inter, sans-serif",
        lineHeight: '16px'
    },
    breakdownValue: {
        color: '#141a17',
        fontSize: 20,
        fontWeight: 500,
        fontFamily: "Roboto, 'Inter', sans-serif",
        lineHeight: '28px',
        margin: 0
    },
});

export default () => {
    const dispatch = useDispatch();
    const [datamonit, setDataMonit] = useState(defaultMonit);

    const accentColor = BaseColor || '#118B50';
    const styles = useMemo(() => createStyles(accentColor), [accentColor]);

    const getMonit = useCallback((value) => {
        dispatch(GetAPI({ url: 'api/v1/monitoring/display', data: value }))
            .then(resp => {
                setDataMonit(resp?.payload?.data || defaultMonit);
            });
    }, [dispatch]);

    useEffect(() => {
        getMonit();
    }, [getMonit]);

    const cards = [
        { key: 'export', title: 'Export', Icon: PlaneIcon },
        { key: 'import', title: 'Import', Icon: InboxIcon },
        { key: 'distribusi', title: 'Distribusi', Icon: RouteIcon }
    ];

    const fmtID = (n) => Number(n || 0).toLocaleString('id-ID');

    const DashboardCard = ({ title, Icon, successCount = 0, errorCount = 0 }) => {
        const total = successCount + errorCount;
        const successRatio = total > 0 ? successCount / total : 1;
        const errorRatio = total > 0 ? errorCount / total : 0;

        return (
            <div style={styles.card()}>
                <div style={styles.cardHeader}>
                    <div style={styles.titleGroup}>
                        <div style={styles.iconChip}>
                            {Icon && <Icon color={accentColor} size={22} />}
                        </div>
                        <p style={styles.cardTitle}>{title}</p>
                    </div>
                    <div style={styles.liveBadge}>
                        <span style={styles.liveDot} />
                        <span style={styles.liveText}>Live</span>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Text style={styles.totalLabel}>Total send data</Text>
                    <p style={styles.totalValue}>{fmtID(total)}</p>
                </div>
                <div style={styles.progressTrack}>
                    <div
                        style={{
                            height: 8,
                            borderRadius: 999,
                            background: accentColor,
                            flexGrow: successRatio,
                            flexShrink: 1,
                            flexBasis: 0,
                            minWidth: total > 0 && successCount > 0 ? 22 : 0
                        }}
                    />
                    <div
                        style={{
                            height: 8,
                            borderRadius: 999,
                            background: '#ef4444',
                            flexGrow: errorRatio,
                            flexShrink: 0,
                            flexBasis: 0,
                            minWidth: errorCount > 0 ? 22 : 0
                        }}
                    />
                </div>
                <div style={styles.breakdown}>
                    <div style={styles.successBox}>
                        <span style={styles.breakdownLabel}>
                            <CheckCircleIcon color={accentColor} size={16} />
                            Success
                        </span>
                        <p style={styles.breakdownValue}>{fmtID(successCount)}</p>
                    </div>
                    <div style={styles.errorBox}>
                        <span style={styles.breakdownLabel}>
                            <DangerCircleIcon color="#ef4444" size={16} />
                            Error
                        </span>
                        <p style={styles.breakdownValue}>{fmtID(errorCount)}</p>
                    </div>
                </div>
            </div>
        );
    };

    const columns = [
        {
            selector: (row) => row?.name,
            name: "Name",
            id: 'name'
        },
        {
            selector: (row) => row?.total,
            name: "Total",
            id: 'total'
        },
    ]

    return (
        <div style={styles.page}>
            <div style={styles.glowOne} />
            <div style={styles.glowTwo} />
            <div style={styles.content}>
                <div style={styles.hero}>
                    <div>
                        <Title level={3} style={styles.heroTitle}>Display Monitoring</Title>
                        <Text style={styles.heroSubtitle}>
                            Monitor the status of sending Export, Import, and Distribution data.
                        </Text>
                    </div>
                    <Button
                        ghost
                        icon={<RedoOutlined />}
                        size="large"
                        style={styles.refreshButton}
                        onClick={() => getMonit()}
                    >
                        Refresh data
                    </Button>
                </div>
                <Row gutter={[24, 24]}>
                    {cards.map(({ key, title, Icon }) => (
                        <Col xs={24} sm={24} md={12} lg={8} key={key}>
                            <DashboardCard
                                title={title}
                                Icon={Icon}
                                successCount={datamonit?.[key]?.success || 0}
                                errorCount={datamonit?.[key]?.error || 0}
                            />
                        </Col>
                    ))}
                </Row>

                <div style={{ marginTop: 40 }}>
                    <TablePage
                        title={"PJT Gate Out"}
                        url="api/v1/tps_import/dashboard_import_out_pjt"
                        actionPosition={'first'}
                        columns={columns}
                    />
                </div>
            </div>
        </div>
    );
};